import { ScoringConfig } from "@common/models/Analytics.model";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { useAnalyticsDeps } from "@main/pages/Release/Release";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

export default function useScoringConfig(metric: string) {
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();
  const { analyticsService } = useAnalyticsDeps();

  const [searchParams] = useSearchParams();
  const selectedVersion = searchParams.get("version") ?? "";

  async function getScoringConfig(signal?: AbortSignal): Promise<ScoringConfig | undefined> {
    if (!selectedWorkspace || !selectedApp) return undefined;

    const { data } = await analyticsService.getScoringConfig(
      selectedWorkspace.id,
      selectedApp.id,
      metric,
      selectedVersion,
      signal,
    );
    return data;
  }

  const {
    data: scoringConfig,
    error: scoringConfigError,
    isLoading: scoringConfigLoading,
  } = useQuery(
    ["scoringConfig_" + metric, { selectedApp, selectedVersion }],
    ({ signal }) => getScoringConfig(signal),
    {
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
    },
  );

  const { mutate: saveScoringConfig, isLoading: isSaving } = useMutation({
    mutationFn: (payload: Record<string, unknown>) => {
      if (!selectedWorkspace || !selectedApp) {
        return Promise.reject(new Error("No workspace or app selected"));
      }
      return analyticsService.putScoringConfig(selectedWorkspace.id, selectedApp.id, metric, payload);
    },
  });

  return {
    scoringConfig,
    scoringConfigLoading,
    scoringConfigError,
    saveScoringConfig,
    isSaving,
  };
}

export const ScoringConfigKeys = {
  network_speed: "network_speed",
  network_health: "network_health",
  testing_coverage: "testing_coverage",
  crash_free_sessions: "crash_free_sessions",
  app_hang_free_sessions: "anr_free_sessions",
  mttr: "mttr",
  resolution_rate: "resolution_rate",
  release_health: "release_health",
};
